/**
 * Ledger Entry Editor - Standalone Contracts
 *
 * Defines all frontend-verifiable types, enums, and mutation operation descriptors
 * for ledger entry lifecycle operations. Enums and input types are derived from
 * authoritative backend GraphQL schema at /home/efren/Repositories/title-graphql/typeDefs/mutations/order/*.graphql
 *
 * Operations covered:
 * - Entry lifecycle: create, update, delete
 * - Association lifecycle: create, update, delete
 * - Party replacement: helpOrderLedgerEntryParties
 * - Linked entry creation: helpOrderLinkedEntry
 * - Posted toggle: updateOrderLedgerPosted
 * - Role deletion: deleteOrderLedgerRole
 *
 * Explicitly NOT included:
 * - updateOrderLedgerStatus (marked unavailable - no wrapper provided)
 */

// ============================================================================
// ENUMS - Authoritative from backend schema
// ============================================================================

/**
 * Order ledger entry kinds.
 * Source: createOrderLedgerEntry.graphql enum OrderLedgerKinds
 */
// eslint-disable-next-line no-shadow -- TS export enum creates value+type binding at same scope
export enum OrderLedgerKinds {
  disbursement = 'disbursement',
  manual = 'manual',
  netfunded = 'netfunded',
  receipt = 'receipt',
}

/**
 * Order ledger entry codes.
 * Source: createOrderLedgerEntry.graphql enum OrderLedgerCodes
 */
// eslint-disable-next-line no-shadow -- TS export enum creates value+type binding at same scope
export enum OrderLedgerCodes {
  cash = 'cash',
  charge = 'charge',
  check = 'check',
  document = 'document',
  line_item = 'line_item',
  netfunded = 'netfunded',
  wire = 'wire',
}

/**
 * Order ledger charges letters.
 * Source: createOrderLedgerEntry.graphql enum OrderLedgerCharges
 */
// eslint-disable-next-line no-shadow -- TS export enum creates value+type binding at same scope
export enum OrderLedgerCharges {
  A = 'A',
  B = 'B',
  C = 'C',
  E = 'E',
  F = 'F',
  G = 'G',
  H = 'H',
  J = 'J',
  K = 'K',
  L = 'L',
  M = 'M',
  N = 'N',
}

// ============================================================================
// INPUT TYPES - Preserve exact schema structure
// ============================================================================

/**
 * Input for creating/updating ledger entry associations.
 * Source: createOrderLedgerAssociation.graphql input OrderLedgerAssociationContent
 */
export type OrderLedgerAssociationContent = {
  amount?: number;
  at_closing_amount?: number;
  at_closing_percent?: number;
  before_closing_amount?: number;
  before_closing_percent?: number;
  payment_id?: string;
  percent?: number;
};

/**
 * Input for updating ledger entry fields.
 * Source: updateOrderLedgerEntry.graphql input OrderLedgerEntryUpdateContent
 */
export type OrderLedgerEntryUpdateContent = {
  amount_override?: number;
  annuality?: number;
  code?: string;
  description?: string;
  entry_date?: string;
  letter?: OrderLedgerCharges;
  number?: number;
  months?: number;
  months_advanced?: number;
};

/**
 * Input for party associations in helpOrderLedgerEntryParties.
 * Source: helpOrderLedgerEntryParties.graphql input OrderEntryPartiesContent
 */
export type OrderEntryPartiesContent = {
  amount?: number;
  at_closing_amount?: number;
  at_closing_percent?: number;
  before_closing_amount?: number;
  before_closing_percent?: number;
  kind: string;
  party_id: string;
  percent?: number;
};

/**
 * Input for entries in helpOrderLedgerEntryParties.
 * Source: helpOrderLedgerEntryParties.graphql input OrderEntriesContent
 */
export type OrderEntriesContent = {
  accounting_id: string;
  parties: OrderEntryPartiesContent[];
};

/**
 * Input for linked entry associations in helpOrderLinkedEntry.
 * Source: helpOrderLinkedEntry.graphql input OrderLinkedEntryContent
 */
export type OrderLinkedEntryContent = {
  target_entry_id: string;
  target_association_id: string;
  target_association_kind: string;
  amount?: number;
};

// ============================================================================
// OPERATION DESCRIPTORS - Define required variables per mutation
// ============================================================================

/**
 * Descriptor for createOrderLedgerEntry mutation.
 * Required variables: _id, amount, code, description, kind
 * Optional: entry_date, letter, number, months, months_advanced, annuality, mortgage_id, filters
 */
export const CREATE_ORDER_LEDGER_ENTRY_OP = {
  name: 'createOrderLedgerEntry',
  requiredVariables: ['_id', 'amount', 'code', 'description', 'kind'] as const,
  optionalVariables: ['entry_date', 'letter', 'number', 'months', 'months_advanced', 'annuality', 'mortgage_id', 'filters'] as const,
} as const;

/**
 * Descriptor for updateOrderLedgerEntry mutation.
 * Required variables: _id, accounting_id, data
 * Optional: filters
 */
export const UPDATE_ORDER_LEDGER_ENTRY_OP = {
  name: 'updateOrderLedgerEntry',
  requiredVariables: ['_id', 'accounting_id', 'data'] as const,
  optionalVariables: ['filters'] as const,
} as const;

/**
 * Descriptor for deleteOrderLedgerEntry mutation.
 * Required variables: _id, accounting_id
 * Optional: filters
 */
export const DELETE_ORDER_LEDGER_ENTRY_OP = {
  name: 'deleteOrderLedgerEntry',
  requiredVariables: ['_id', 'accounting_id'] as const,
  optionalVariables: ['filters'] as const,
} as const;

/**
 * Descriptor for createOrderLedgerAssociation mutation.
 * Required variables: _id, accounting_id, data, kind, party_id
 * Optional: filters
 */
export const CREATE_ORDER_LEDGER_ASSOCIATION_OP = {
  name: 'createOrderLedgerAssociation',
  requiredVariables: ['_id', 'accounting_id', 'data', 'kind', 'party_id'] as const,
  optionalVariables: ['filters'] as const,
} as const;

/**
 * Descriptor for updateOrderLedgerAssociation mutation.
 * Required variables: _id, accounting_id, data, kind, party_id
 * Optional: filters
 */
export const UPDATE_ORDER_LEDGER_ASSOCIATION_OP = {
  name: 'updateOrderLedgerAssociation',
  requiredVariables: ['_id', 'accounting_id', 'data', 'kind', 'party_id'] as const,
  optionalVariables: ['filters'] as const,
} as const;

/**
 * Descriptor for deleteOrderLedgerAssociation mutation.
 * Required variables: _id, accounting_id, kind, party_id
 * Optional: filters
 */
export const DELETE_ORDER_LEDGER_ASSOCIATION_OP = {
  name: 'deleteOrderLedgerAssociation',
  requiredVariables: ['_id', 'accounting_id', 'kind', 'party_id'] as const,
  optionalVariables: ['filters'] as const,
} as const;

/**
 * Descriptor for helpOrderLedgerEntryParties mutation.
 * Required variables: _id, data
 * Optional: filters
 */
export const HELP_ORDER_LEDGER_ENTRY_PARTIES_OP = {
  name: 'helpOrderLedgerEntryParties',
  requiredVariables: ['_id', 'data'] as const,
  optionalVariables: ['filters'] as const,
} as const;

/**
 * Descriptor for helpOrderLinkedEntry mutation.
 * Required variables: _id, code, description, entry_date, kind, targets
 * Optional: posted, filters
 */
export const HELP_ORDER_LINKED_ENTRY_OP = {
  name: 'helpOrderLinkedEntry',
  requiredVariables: ['_id', 'code', 'description', 'entry_date', 'kind', 'targets'] as const,
  optionalVariables: ['posted', 'filters'] as const,
} as const;

/**
 * Descriptor for updateOrderLedgerPosted mutation.
 * Required variables: _id, accounting_id, posted
 */
export const UPDATE_ORDER_LEDGER_POSTED_OP = {
  name: 'updateOrderLedgerPosted',
  requiredVariables: ['_id', 'accounting_id', 'posted'] as const,
  optionalVariables: [] as const,
} as const;

/**
 * Descriptor for deleteOrderLedgerRole mutation.
 * Required variables: _id, accounting_ids, role
 * Optional: filters
 */
export const DELETE_ORDER_LEDGER_ROLE_OP = {
  name: 'deleteOrderLedgerRole',
  requiredVariables: ['_id', 'accounting_ids', 'role'] as const,
  optionalVariables: ['filters'] as const,
} as const;

// ============================================================================
// STATUS MUTATION - EXPLICITLY UNAVAILABLE
// ============================================================================

/**
 * updateOrderLedgerStatus is NOT supported by this editor.
 * Status updates are backend-only operations that require special authorization.
 * No wrapper is provided; attempting to use this operation will fail.
 *
 * If status updates are needed, they must be handled through a separate,
 * dedicated service with explicit authorization checks.
 */
export const UPDATE_ORDER_LEDGER_STATUS_UNAVAILABLE = {
  name: 'updateOrderLedgerStatus',
  status: 'unavailable',
  reason: 'Backend-only operation requiring special authorization',
} as const;

// ============================================================================
// MUTATION PAYLOAD TYPES - For service layer
// ============================================================================

export type CreateOrderLedgerEntryPayload = {
  _id: string;
  amount: number;
  code: OrderLedgerCodes;
  description: string;
  kind: OrderLedgerKinds;
  entry_date?: string;
  letter?: OrderLedgerCharges;
  number?: number;
  months?: number;
  months_advanced?: number;
  annuality?: number;
  mortgage_id?: string;
  filters?: Record<string, unknown>;
};

export type UpdateOrderLedgerEntryPayload = {
  _id: string;
  accounting_id: string;
  data: OrderLedgerEntryUpdateContent;
  filters?: Record<string, unknown>;
};

export type DeleteOrderLedgerEntryPayload = {
  _id: string;
  accounting_id: string;
  filters?: Record<string, unknown>;
};

export type CreateOrderLedgerAssociationPayload = {
  _id: string;
  accounting_id: string;
  data: OrderLedgerAssociationContent;
  kind: string;
  party_id: string;
  filters?: Record<string, unknown>;
};

export type UpdateOrderLedgerAssociationPayload = {
  _id: string;
  accounting_id: string;
  data: OrderLedgerAssociationContent;
  kind: string;
  party_id: string;
  filters?: Record<string, unknown>;
};

export type DeleteOrderLedgerAssociationPayload = {
  _id: string;
  accounting_id: string;
  kind: string;
  party_id: string;
  filters?: Record<string, unknown>;
};

export type HelpOrderLedgerEntryPartiesPayload = {
  _id: string;
  data: OrderEntriesContent[];
  filters?: Record<string, unknown>;
};

export type HelpOrderLinkedEntryPayload = {
  _id: string;
  code: OrderLedgerCodes;
  description: string;
  entry_date: string;
  kind: OrderLedgerKinds;
  targets: OrderLinkedEntryContent[];
  posted?: boolean;
  filters?: Record<string, unknown>;
};

export type UpdateOrderLedgerPostedPayload = {
  _id: string;
  accounting_id: string;
  posted: boolean;
};

export type DeleteOrderLedgerRolePayload = {
  _id: string;
  accounting_ids: string[];
  role: string;
  filters?: Record<string, unknown>;
};

// ============================================================================
// RETURN TYPES - All mutations return Order
// ============================================================================

export type OrderMutationReturn = {
  [key: string]: unknown;
};
